import React, { createContext, useContext, useState, useEffect } from "react";
import {
  User,
  login as apiLogin,
  logout as apiLogout,
  changePassword as apiChangePassword,
  getStoredUser,
  storeAuth,
  isAuthenticated as checkAuth,
} from "../repository/authRepository";
import { setupAutoRefresh, clearAutoRefresh } from "../utils/tokenRefresh";

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isCommissioner: boolean;
  mustChangePassword: boolean;
  login: (username: string, password: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
  error: string | null;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const storedUser = getStoredUser();
    if (storedUser && checkAuth()) {
      setUser(storedUser);
      setupAutoRefresh();
    }
    setLoading(false);
    return () => clearAutoRefresh();
  }, []);

  const login = async (username: string, password: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiLogin(username, password);
      storeAuth(response);
      setUser(response.user);
      setupAutoRefresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Login failed";
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    setLoading(true);
    setError(null);
    try {
      await apiChangePassword(currentPassword, newPassword);
      if (user) {
        const updatedUser = { ...user, must_change_password: false };
        setUser(updatedUser);
        localStorage.setItem("user", JSON.stringify(updatedUser));
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to change password";
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    clearAutoRefresh();
    apiLogout();
    setUser(null);
  };

  const clearError = () => setError(null);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isCommissioner: user?.is_commissioner ?? false,
    mustChangePassword: user?.must_change_password ?? false,
    login,
    changePassword,
    logout,
    loading,
    error,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
